import {IssueRelativeTime} from '@github-ui/ago'
import {MarkdownViewer} from '@github-ui/markdown-viewer'
import {sanitize, type SanitizedHTML} from '@github-ui/sanitize'
import {ScrollableRegion} from '@primer/react/experimental'
import {useCallback, useMemo, useState} from 'react'

import type {ScribeArtifactVersion} from '../types'
import styles from './ArtifactPanelContent.module.css'

export type ArtifactType = 'plan' | 'link' | 'generic'

export type PanelArtifact = {
  id: string
  number?: number
  type: ArtifactType
  name: string
  body: string
  bodyHTML: string
  trustedBodyHTML?: SanitizedHTML
  createdAt?: string
  updatedAt?: string
  hasEdits?: boolean
  updatedByActor?: {
    login: string
  }
  resourceType?: 'issue'
  resourceId?: string | number
}

type ArtifactPanelBodyProps = {
  artifact: PanelArtifact
  selectedVersion?: ScribeArtifactVersion | null
}

/** Just the rendered markdown (or plain-text fallback) for an artifact. */
export function ArtifactPanelBody({artifact, selectedVersion = null}: ArtifactPanelBodyProps) {
  const displayedBody = selectedVersion ? (selectedVersion.body ?? '') : artifact.body
  const displayedBodyHTML = selectedVersion ? (selectedVersion.body_html ?? '') : artifact.bodyHTML
  const trustedBodyHTML = selectedVersion ? (selectedVersion.body_html ?? undefined) : artifact.trustedBodyHTML
  const displayedAt = selectedVersion ? (selectedVersion.created_at ?? undefined) : artifact.updatedAt
  const displayedActor = selectedVersion ? (selectedVersion.actor ?? undefined) : artifact.updatedByActor
  const sanitizedHTML = useMemo(
    () => trustedBodyHTML ?? (displayedBodyHTML ? sanitize(displayedBodyHTML) : ''),
    [displayedBodyHTML, trustedBodyHTML],
  )

  return (
    <ScrollableRegion key={artifact.id} className={styles.Body} aria-label={artifact.name}>
      {displayedAt && (
        <div className={styles.Metadata}>
          <span className={styles.MetadataText}>
            <span>{selectedVersion ? 'Edited' : 'Updated'}</span> <IssueRelativeTime datetime={displayedAt} />
            {displayedActor && <span> by {displayedActor.login}</span>}
          </span>
        </div>
      )}
      {sanitizedHTML ? (
        <div className={styles.MarkdownContent}>
          <MarkdownViewer verifiedHTML={sanitizedHTML} />
        </div>
      ) : (
        <pre className={styles.PlainBody}>{displayedBody}</pre>
      )}
    </ScrollableRegion>
  )
}

export function useArtifactVersionSelection(artifact: PanelArtifact | null) {
  const [selection, setSelection] = useState<{artifactId: string; version: ScribeArtifactVersion} | null>(null)
  const artifactId = artifact?.id
  const selectedVersion = selection && selection.artifactId === artifactId ? selection.version : null
  const selectVersion = useCallback(
    (version: ScribeArtifactVersion) => {
      if (artifactId) setSelection({artifactId, version})
    },
    [artifactId],
  )

  return {selectedVersion, selectVersion}
}
